import { API_URL } from '../config/api';

class ActivityService {
  constructor() {
    this.user = null;
    this.store = null;
    this.sessionId = this.generateSessionId();
    this.activityQueue = [];
    this.flushInterval = null;
    this.currentPage = null;
    this.pageEnterTime = null;
    this.isInitialized = false;
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  setContext(user, store) {
    this.user = user;
    this.store = store;

    if (!this.isInitialized && user) {
      this.startFlushInterval();
      this.isInitialized = true;
    }
  }

  startFlushInterval() {
    // Her 10 saniyede bir aktiviteleri gönder
    this.flushInterval = setInterval(() => {
      this.flush();
    }, 10000);

    // Sayfa kapanırken kalan aktiviteleri gönder
    window.addEventListener('beforeunload', () => {
      this.flush(true);
    });
  }

  getBaseData() {
    return {
      user_id: this.user?.id,
      user_name: this.user?.name,
      user_role: this.user?.role,
      store_id: this.store?.id,
      store_name: this.store?.name,
      session_id: this.sessionId,
      ip_address: null, // Backend'de alınacak
      user_agent: navigator.userAgent,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight
    };
  }

  // Aktivite ekle (queue'ya)
  track(activityType, data = {}) {
    if (!this.user) return;

    const activity = {
      ...this.getBaseData(),
      activity_type: activityType,
      page_name: data.page_name || this.currentPage,
      page_path: data.page_path || window.location.pathname,
      element_id: data.element_id,
      element_text: data.element_text?.substring(0, 255),
      element_type: data.element_type,
      details: data.details,
      duration_ms: data.duration_ms
    };

    this.activityQueue.push(activity);

    // 50'den fazla aktivite birikirse hemen gönder
    if (this.activityQueue.length >= 50) {
      this.flush();
    }
  }

  // Sayfa görüntüleme
  pageView(pageName, pagePath) {
    // Önceki sayfa için süreyi hesapla
    if (this.currentPage && this.pageEnterTime) {
      const duration = Date.now() - this.pageEnterTime;
      this.track('page_leave', {
        page_name: this.currentPage,
        duration_ms: duration
      });
    }

    this.currentPage = pageName;
    this.pageEnterTime = Date.now();

    this.track('page_view', {
      page_name: pageName,
      page_path: pagePath || window.location.pathname
    });
  }

  // Tıklama
  click(elementId, elementText, elementType, details = {}) {
    this.track('click', {
      element_id: elementId,
      element_text: elementText,
      element_type: elementType,
      details
    });
  }

  // Buton tıklama
  buttonClick(buttonText, details = {}) {
    this.track('button_click', {
      element_text: buttonText,
      element_type: 'button',
      details
    });
  }

  // Form gönderimi
  formSubmit(formName, details = {}) {
    this.track('form_submit', {
      element_id: formName,
      element_type: 'form',
      details
    });
  }

  // Arama
  search(searchTerm, resultCount = null) {
    this.track('search', {
      details: {
        search_term: searchTerm,
        result_count: resultCount
      }
    });
  }

  // Filtre uygulama
  filter(filterName, filterValue) {
    this.track('filter', {
      details: {
        filter_name: filterName,
        filter_value: filterValue
      }
    });
  }

  // Modal açma
  modalOpen(modalName) {
    this.track('modal_open', {
      element_id: modalName,
      element_type: 'modal'
    });
  }

  // Modal kapatma
  modalClose(modalName) {
    this.track('modal_close', {
      element_id: modalName,
      element_type: 'modal'
    });
  }

  // Tab değişikliği
  tabChange(tabName, fromTab = null) {
    this.track('tab_change', {
      element_id: tabName,
      element_type: 'tab',
      details: { from_tab: fromTab }
    });
  }

  // İndirme
  download(fileName, fileType) {
    this.track('download', {
      details: {
        file_name: fileName,
        file_type: fileType
      }
    });
  }

  // Yazdırma
  print(documentType, details = {}) {
    this.track('print', {
      details: {
        document_type: documentType,
        ...details
      }
    });
  }

  // Özel aktivite
  custom(activityName, details = {}) {
    this.track('other', {
      element_text: activityName,
      details
    });
  }

  // Aktiviteleri sunucuya gönder
  async flush(sync = false) {
    if (this.activityQueue.length === 0) return;

    const activitiesToSend = [...this.activityQueue];
    this.activityQueue = [];

    try {
      // Sayfa kapanırken (sync) de fetch + keepalive kullanılır: sendBeacon oturum token'ı gönderemiyor
      await fetch(`${API_URL}/activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activities: activitiesToSend }),
        keepalive: sync
      });
    } catch (error) {
      console.error('Aktivite gönderme hatası:', error);
      // Başarısız olanları tekrar queue'ya ekle
      this.activityQueue = [...activitiesToSend, ...this.activityQueue];
    }
  }

  // Temizlik
  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flush(true);
  }
}

// Singleton instance
const activityService = new ActivityService();

export default activityService;

// Sayfa isimleri (Türkçe)
export const PAGE_NAMES = {
  '/': 'Ana Sayfa',
  '/sales': 'Perakende Satış',
  '/products': 'Ürünler',
  '/customers': 'Müşteriler',
  '/repairs': 'Tamir Takip',
  '/campaigns': 'Kampanyalar',
  '/inventory': 'Sayım',
  '/scrap': 'Hurda Alım',
  '/reports': 'Raporlar',
  '/settings': 'Ayarlar',
  '/personnel': 'Personel',
  '/payments': 'Ödeme Takvimi',
  '/wholesalers': 'Toptancılar',
  '/shopify': 'Shopify',
  '/terminal': 'El Terminali',
  '/legal': 'Yasal Bilgiler'
};

// Aktivite tipi isimleri (Türkçe)
export const ACTIVITY_TYPE_NAMES = {
  page_view: 'Sayfa Görüntüleme',
  page_leave: 'Sayfadan Çıkış',
  click: 'Tıklama',
  form_submit: 'Form Gönderimi',
  search: 'Arama',
  filter: 'Filtreleme',
  modal_open: 'Modal Açma',
  modal_close: 'Modal Kapatma',
  button_click: 'Buton Tıklama',
  tab_change: 'Sekme Değişikliği',
  scroll: 'Kaydırma',
  download: 'İndirme',
  print: 'Yazdırma',
  copy: 'Kopyalama',
  other: 'Diğer'
};
